import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { AutoComplete } from "primereact/autocomplete";
import { Button } from "primereact/button";
import "../styles/CrearPedidoModal.css";
import LoadingOverlay from "../Componentes/LoadingOverlay";
import { fetchWithAuth, API_URL } from "../Componentes/fetchWithAuth";

//const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const translations = {
  es: {
    titulo: "Crear Pedido",
    cliente: "Cliente",
    fechaPedido: "Fecha del Pedido",
    observaciones: "Observaciones",
    guardar: "Guardar",
    cancelar: "Cancelar",
    placeholderCliente: "Busca un cliente activo...",
  },
  en: {
    titulo: "Create Order",
    cliente: "Client",
    fechaPedido: "Order Date",
    observaciones: "Notes",
    guardar: "Save",
    cancelar: "Cancel",
    placeholderCliente: "Search an active client...",
  },
  zh: {
    titulo: "创建订单",
    cliente: "客户",
    fechaPedido: "订单日期",
    observaciones: "备注",
    guardar: "保存",
    cancelar: "取消",
    placeholderCliente: "搜索一个活跃的客户...",
  },
};

const CrearPedidoModal = ({ isOpen, onClose, language, onSave }) => {
  const [formData, setFormData] = useState({
    iCodigoCliente: null, // ID del cliente seleccionado
    sNombreCliente: "", // Nombre del cliente seleccionado
    fecha_pedido: "", // Fecha del pedido seleccionada
    observaciones: "", // Observaciones ingresadas
    estadoPedido: 1, // Estado del pedido por defecto
  });

  const [clientes, setClientes] = useState([]); // Clientes filtrados para el AutoComplete
  const [allClientes, setAllClientes] = useState([]); // Todos los clientes cargados inicialmente
  const [isLoading, setIsLoading] = useState(false); // [EXPERT_ANALYSIS]: QUÉ - Declaración de nuevo estado para controlar el envío del formulario. - POR QUÉ - Previene múltiples envíos al deshabilitar el botón "Guardar" mientras la operación está en curso. - IMPACTO - Mejora la UX y la integridad de los datos.
  const t = translations[language] || translations.es;

  // Configurar la fecha actual al abrir el modal y cargar todos los clientes
  useEffect(() => {
    if (isOpen) {
      const now = new Date();
      const fechaActual = now.toISOString().slice(0, 16);
      setFormData((prev) => ({ ...prev, fecha_pedido: fechaActual }));

      // Cargar todos los clientes activos una única vez al abrir el modal
      const fetchAllClientes = async () => {
        try {
          const response = await fetchWithAuth(
            `${API_URL}/api/clientes-activos`,
          ); // Sin parámetro 'q'
          const data = await response.json();
          const clientesFormateados = data.map((cliente) => ({
            ...cliente,
            label: `${cliente.sNombreCliente} (${cliente.sMarca})`,
          }));
          setAllClientes(clientesFormateados);
        } catch (error) {
          console.error("Error al cargar todos los clientes:", error);
        }
      };
      fetchAllClientes();
    }
  }, [isOpen]);

  // Método para buscar clientes activos (ahora filtra localmente)
  const buscarClientes = (event) => {
    // Ya no es async
    const query = event.query.trim().toLowerCase();
    console.log("Texto para buscar (local):", query);

    if (query.length < 2) {
      setClientes([]);
      return;
    }

    // Filtrar clientes desde allClientes
    const filteredClientes = allClientes.filter(
      (cliente) =>
        cliente.sNombreCliente.toLowerCase().includes(query) ||
        cliente.sMarca.toLowerCase().includes(query),
    );

    setClientes(filteredClientes);
  };

  const handleClienteSelect = (e) => {
    const clienteSeleccionado = e.value;
    console.log("Cliente seleccionado:", clienteSeleccionado);

    setFormData({
      ...formData,
      iCodigoCliente: clienteSeleccionado.iCodigo,
      sNombreCliente: clienteSeleccionado.sNombreCliente, // Solo guardar el nombre
    });
  };

  const handleSave = () => {
    // [EXPERT_ANALYSIS]: QUÉ - Validación de estado de carga. - POR QUÉ - Evita que la función se ejecute si ya hay un envío en progreso, previniendo duplicados. - IMPACTO - Mejora la robustez del formulario.
    if (isLoading) {
      return;
    }

    if (!formData.iCodigoCliente) {
      alert(t.cliente + " es obligatorio");
      return;
    }

    setIsLoading(true); // [EXPERT_ANALYSIS]: QUÉ - Establece isLoading a true. - POR QUÉ - Indica que la operación de guardado ha comenzado, deshabilitando el botón. - IMPACTO - Previene envíos duplicados.

    const payload = {
      iCodigoCliente: formData.iCodigoCliente,
      fecha_pedido: formData.fecha_pedido,
      observaciones: formData.observaciones,
      estadoPedido: formData.estadoPedido,
    };

    console.log("Guardando pedido con datos:", payload);

    fetchWithAuth(`${API_URL}/api/pedidos`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (!response.ok) throw new Error("Error al guardar el pedido");
        return response.json();
      })
      .then(() => {
        //alert("Pedido guardado con éxito");
        if (onSave) onSave(); // Llama a onSave para refrescar la tabla
        onClose();
      })
      .catch((err) => console.error("Error al guardar el pedido:", err))
      .finally(() => {
        setIsLoading(false); // [EXPERT_ANALYSIS]: QUÉ - Restablece isLoading a false. - POR QUÉ - Asegura que el botón se habilite nuevamente después de que la promesa se resuelva (éxito o error). - IMPACTO - Permite al usuario intentar de nuevo o realizar otras acciones.
      });
  };

  return (
    <Dialog
      header={t.titulo}
      visible={isOpen}
      style={{ width: "40vw" }}
      modal
      onHide={onClose}
    >
      {isLoading && <LoadingOverlay message="Guardando pedido..." />}
      <div className="p-fluid">
        <div className="field">
          <label htmlFor="cliente">{t.cliente}</label>
          <AutoComplete
            id="pedido-cliente-input" // ID descriptivo para el log
            value={formData.sNombreCliente}
            suggestions={clientes}
            completeMethod={buscarClientes}
            field="label"
            placeholder={t.placeholderCliente}
            onChange={(e) => {
              const newName = e.target.value;
              setFormData((prev) => ({
                ...prev,
                sNombreCliente: newName,
                iCodigoCliente:
                  newName === ""
                    ? null
                    : prev.sNombreCliente === newName
                      ? prev.iCodigoCliente
                      : null,
              }));
            }}
            onSelect={handleClienteSelect}
            className="p-inputtext"
            dropdown={false}
            loading={isLoading}
          />
        </div>
        <div className="field">
          <label htmlFor="fecha_pedido">{t.fechaPedido}</label>
          <input
            type="datetime-local"
            id="pedido-fecha-input" // ID descriptivo
            name="fecha_pedido"
            value={formData.fecha_pedido}
            onChange={(e) =>
              setFormData({ ...formData, fecha_pedido: e.target.value })
            }
            className="p-inputtext"
          />
        </div>
        <div className="field">
          <label htmlFor="observaciones">{t.observaciones}</label>
          <textarea
            id="pedido-observaciones-textarea" // ID descriptivo
            name="observaciones"
            value={formData.observaciones}
            onChange={(e) =>
              setFormData({ ...formData, observaciones: e.target.value })
            }
            rows={6}
            className="p-inputtextarea"
            style={{ width: "100%" }}
          ></textarea>
        </div>
        <div className="p-dialog-footer">
          <Button
            id="btn-crear-pedido-guardar" // ID único y descriptivo
            label={t.guardar}
            icon="pi pi-check"
            className="p-button-success"
            onClick={handleSave}
            disabled={isLoading}
          />
          <Button
            id="btn-crear-pedido-cancelar" // ID único y descriptivo
            label={t.cancelar}
            icon="pi pi-times"
            className="p-button-secondary"
            onClick={onClose}
            disabled={isLoading}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default CrearPedidoModal;
